"use client"

import * as React from "react"

interface AlphabetRailProps {
  /** Set of letters that have at least one brand in the current filter. */
  activeLetters: Set<string>
  /** Currently in-view letter, highlighted in orange. */
  currentLetter: string | null
  /** Called when the user clicks an active letter — caller smooth-scrolls. */
  onJump: (letter: string) => void
  /** Hide rail on mobile (handoff implementation checklist note). Default true. */
  hideOnMobile?: boolean
}

const LETTERS = [..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split(""), "#"]

/**
 * A–Z Quick-Jump Rail (handoff §4).
 *
 * Horizontal pill with one circular button per letter + a `#` bucket for
 * numeric/symbol brand names. Letters with no matching brand are disabled
 * (opacity 0.3, pointer-events: none). The current in-view letter is
 * highlighted with the orange brand color.
 *
 * Sticky scroll position tracking lives in the parent — this component is
 * presentational and forwards clicks via `onJump`. Hidden under sm to keep
 * the mobile toolbar uncluttered.
 */
export function AlphabetRail({
  activeLetters,
  currentLetter,
  onJump,
  hideOnMobile = true,
}: AlphabetRailProps) {
  return (
    <nav
      aria-label="Jump to letter"
      className={`mx-auto mb-6 inline-flex flex-wrap justify-center gap-[2px] rounded-full border border-gray-200 bg-white/70 p-1.5 backdrop-blur-md ${
        hideOnMobile ? "hidden sm:inline-flex" : ""
      }`}
    >
      {LETTERS.map(L => {
        const active = activeLetters.has(L)
        const current = currentLetter === L
        return (
          <button
            key={L}
            type="button"
            onClick={() => active && onJump(L)}
            disabled={!active}
            aria-label={`Jump to brands starting with ${L === "#" ? "a number or symbol" : L}`}
            aria-current={current ? "true" : undefined}
            className={`grid h-7 w-7 place-items-center rounded-full text-[12px] font-bold transition-all duration-150 ${
              !active
                ? "pointer-events-none opacity-30 text-gray-600"
                : current
                  ? "bg-[#ff9951] text-[var(--color-brand-action-text)]"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            }`}
          >
            {L}
          </button>
        )
      })}
    </nav>
  )
}
